import { type CSSProperties, type MouseEvent as ReactMouseEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { LogLevel } from "whole-shared";

import { createCleaner } from "../../config";
import { hud } from "./HudController";
import type { DevStats } from "./stats";

const MIN_WIDTH = 400;
const MIN_HEIGHT = 200;

const rootFrameStyle: CSSProperties = {
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display:         "flex",
  flexDirection:   "column",
  overflow:        "hidden",
  pointerEvents:   "none",
  position:        "fixed",
  zIndex:          5
};

const statsRowStyle: CSSProperties = {
  flexShrink: 0,
  height:     48,
  overflow:   "hidden"
};

const logsScrollStyle: CSSProperties = {
  boxSizing:     "border-box",
  flex:          "1 1 0",
  minHeight:     0,
  overflowX:     "hidden",
  overflowY:     "auto",
  pointerEvents: "none"
};

const logsInnerStyle: CSSProperties = {
  boxSizing:      "border-box",
  display:        "flex",
  flexDirection:  "column",
  justifyContent: "flex-end",
  minHeight:      "100%",
  minWidth:       0
};

const titleBarStyle: CSSProperties = {
  alignItems:      "center",
  backgroundColor: "rgba(80, 80, 120, 0.85)",
  boxSizing:       "border-box",
  cursor:          "grab",
  display:         "flex",
  flexShrink:      0,
  justifyContent:  "space-between",
  minHeight:       28,
  pointerEvents:   "auto",
  userSelect:      "none",
  width:           "100%"
};

const titleBarControlsStyle: CSSProperties = {
  alignItems:    "center",
  cursor:        "default",
  display:       "flex",
  flexShrink:    0,
  gap:           12,
  pointerEvents: "auto",
  userSelect:    "none"
};

const titleBarCheckboxLabelStyle: CSSProperties = {
  alignItems:    "center",
  cursor:        "default",
  display:       "flex",
  flexShrink:    0,
  gap:           6,
  pointerEvents: "auto",
  userSelect:    "none"
};

const resizeHandleStyle: CSSProperties = {
  backgroundColor: "rgba(120, 120, 180, 0.9)",
  bottom:          0,
  cursor:          "nwse-resize",
  height:          28,
  pointerEvents:   "auto",
  position:        "absolute",
  right:           0,
  userSelect:      "none",
  width:           28,
  zIndex:          2
};

interface Position {
  left: number;
  top: number;
}

interface Dimensions extends Position {
  height: number;
  width: number;
}

const stringify = (arg: unknown) => {
  switch(typeof arg) {
  case "string":
    return arg;
  case "bigint":
  case "boolean":
  case "number":
    return arg.toString();
  case "function":
    return "function";
  case "symbol":
    return "symbol";
  case "undefined":
    return "undefined";
  default:
    if(arg === null) return "null";
    if(arg === undefined) return "undefined";
    if(arg instanceof Error) return arg.stack;
    return JSON.stringify(arg);
  }
};

const levelColor = (level: LogLevel) => {
  switch(level) {
  case "debug":
    return "rgba(255, 255, 255, 0.5)";
  case "error":
    return "rgba(255, 0, 0, 0.5)";
  case "info":
    return "rgba(255, 255, 255, 0.5)";
  case "warn":
    return "rgba(255, 255, 0, 0.5)";
  }
};

export const DevWindow = ({ devStats }: { devStats: DevStats }) => {
  const [logs, setLogs] = useState<[LogLevel, unknown[]][]>([]);
  const [lines, setLines] = useState<[LogLevel, string][]>([]);
  const [visible, setVisible] = useState(true);
  const [logWrap, setLogWrap] = useState(true);
  const [showDebugLines, setShowDebugLines] = useState(true);
  const statsRef = useRef<HTMLDivElement>(null);
  const logRef = useRef<HTMLDivElement>(null);
  const stickLogRef = useRef(true);
  const [dimensions, setDimensions] = useState<Dimensions>({
    height: Math.min(MIN_HEIGHT, window.innerHeight),
    left:   0,
    top:    0,
    width:  Math.min(MIN_WIDTH, window.innerWidth)
  });
  const dimensionsRef = useRef<Dimensions>(dimensions);
  const interactionRef = useRef<"move" | "resize" | null>(null);
  const downDimensionsRef = useRef<Dimensions>({ height: 0, left: 0, top: 0, width: 0 });
  const downPositionRef = useRef<Position>({ left: 0, top: 0 });

  const onMouseDown = useCallback(
    (mode: "move" | "resize") => (e: ReactMouseEvent<HTMLDivElement>) => {
      if(e.button !== 0) return;

      e.preventDefault();
      e.stopPropagation();

      downDimensionsRef.current = { ...dimensionsRef.current };
      downPositionRef.current = { left: e.clientX, top: e.clientY };
      interactionRef.current = mode;
    },
    []
  );

  /* eslint-disable react-hooks/exhaustive-deps, react-hooks/use-memo */
  const onResize = useCallback(onMouseDown("resize"), []);
  const onMove = useCallback(onMouseDown("move"), []);
  /* eslint-enable react-hooks/exhaustive-deps, react-hooks/use-memo */

  useEffect(() => {
    const cleaner = createCleaner();

    hud.emit("logAttached", true);
    hud.on("devWindow", cleaner, setVisible);
    hud.on("log", cleaner, (level, ...args) => setLogs(prev => [...prev, [level, args]]));

    return () => {
      cleaner.clean();
      hud.emit("logAttached", false);
    };
  }, []);

  useEffect(() => {
    if(logs.length === 0) return;

    const element = logRef.current;

    if(element) {
      const { clientHeight, scrollHeight, scrollTop } = element;
      const epsilon = 2;

      stickLogRef.current = scrollHeight <= clientHeight + epsilon || scrollTop + clientHeight >= scrollHeight - epsilon;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLogs([]);
    setLines(prev => [
      ...prev,
      ...logs.flatMap(([level, args]) => {
        const text = args.map(stringify).join(" ");

        return text.split(/\r?\n/).map(part => [level, part] as [LogLevel, string]);
      })
    ]);
  }, [logs]);

  useLayoutEffect(() => {
    const element = logRef.current;

    if(! element || ! stickLogRef.current) return;

    element.scrollTop = element.scrollHeight;
  }, [lines]);

  useEffect(() => {
    if(! visible) return;

    const onMove = (e: MouseEvent) => {
      if(interactionRef.current === null) return;

      const offsetX = e.clientX - downPositionRef.current.left;
      const offsetY = e.clientY - downPositionRef.current.top;
      const { height, left, top, width } = downDimensionsRef.current;
      const { current } = dimensionsRef;

      if(interactionRef.current === "move") {
        current.left = Math.min(Math.max(0, left + offsetX), window.innerWidth - width);
        current.top = Math.min(Math.max(0, top + offsetY), window.innerHeight - height);
      } else {
        current.height = Math.min(Math.max(MIN_HEIGHT, height + offsetY), window.innerHeight - top);
        current.width = Math.min(Math.max(MIN_WIDTH, width + offsetX), window.innerWidth - left);
      }

      setDimensions({ ...current });
    };

    const onResize = () => {
      const { innerHeight, innerWidth } = window;
      let { height, left, top, width } = dimensionsRef.current;

      width = Math.min(width, innerWidth);
      height = Math.min(height, innerHeight);
      left = Math.min(Math.max(0, left), Math.max(0, innerWidth - width));
      top = Math.min(Math.max(0, top), Math.max(0, innerHeight - height));
      width = Math.min(width, innerWidth - left);
      height = Math.min(height, innerHeight - top);

      setDimensions((dimensionsRef.current = { height, left, top, width }));
    };

    const onUp = () => {
      interactionRef.current = null;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("resize", onResize);
    };
  }, [visible]);

  useEffect(() => {
    if(! visible) return;

    const children = Array.from(devStats.stats.dom.children);
    const container = statsRef.current;

    if(! container) return;

    for(const child of children) {
      (child as HTMLElement).style.display = "inline-block";
      container.appendChild(child);
    }

    return () => {
      for(const child of children) devStats.stats.dom.appendChild(child);
    };
  }, [devStats, visible]);

  useEffect(() => {
    if(! visible) return;

    const el = logRef.current;

    if(! el) return;

    const onWheel = (e: WheelEvent) => {
      const rect = el.getBoundingClientRect();

      if(e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) return;

      el.scrollTop += e.deltaY;
      e.preventDefault();
      e.stopImmediatePropagation();
    };

    window.addEventListener("wheel", onWheel, { capture: true, passive: false });

    return () => {
      window.removeEventListener("wheel", onWheel, { capture: true });
    };
  }, [visible]);

  return (
    visible && <div style={{ ...rootFrameStyle, ...dimensions }}>
      <div onMouseDown={onMove} style={titleBarStyle}>
        <span>{"Dev"}</span>
        <div onMouseDown={e => e.stopPropagation()} style={titleBarControlsStyle}>
          <button
            onClick={() => {
              setLines([]);
              stickLogRef.current = true;
            }}
            onMouseDown={e => e.stopPropagation()}
            style={{
              background:    "transparent",
              border:        "none",
              color:         "inherit",
              cursor:        "pointer",
              font:          "inherit",
              padding:       "0 4px",
              pointerEvents: "auto"
            }}
            type="button"
          >
            {"clear"}
          </button>
          <label style={titleBarCheckboxLabelStyle}>
            {"wrap"}
            <input checked={logWrap} onChange={e => setLogWrap(e.target.checked)} type="checkbox" />
          </label>
          <label style={titleBarCheckboxLabelStyle}>
            {"debug"}
            <input checked={showDebugLines} onChange={e => setShowDebugLines(e.target.checked)} type="checkbox" />
          </label>
        </div>
      </div>
      <div ref={statsRef} style={statsRowStyle} />
      <div ref={logRef} style={logsScrollStyle}>
        <div style={logsInnerStyle}>
          {lines.map(([level, line], idx) => {
            if(! showDebugLines && level === "debug") return null;

            return <div
              key={idx}
              style={{
                borderBottom: "1px solid",
                color:        levelColor(level),
                minWidth:     0,
                ...(! logWrap ? { overflow: "hidden", whiteSpace: "nowrap" } : {})
              }}
            >
              {line}
            </div>;
          })}
        </div>
      </div>
      <div onMouseDown={onResize} style={resizeHandleStyle} />
    </div>
  );
};
